/**
 * Pure helpers for the list-level Quran (Hifz) status surfaces on GroupsPage
 * and StudentsPage. They take the rollup payloads returned by
 * quranApi.studentStatus / quranApi.groupStatus and derive the small bits the
 * list columns and filters need: % of goal, on-track vs behind, per-surah
 * status presence, and group averages.
 *
 * Framework-free on purpose so they can be unit-tested directly
 * (quranStatus.test.js) and reused by both pages without duplication. Mirrors
 * the rollup shape from backend/routes/quran.js statusForStudent():
 *   { hasGoal, percent, goalSurahNumbers, surahs:[{number,status}],
 *     homework:{ total, done, missed }, lastActivity }
 */

// Per-surah mastery statuses, in the order surfaced by the status filter.
export const MASTERY_STATUSES = ['mastered', 'in-progress', 'needs-review', 'not-started'];

// status key (hyphenated) -> quran.* i18n suffix (camelCase).
export const STATUS_KEY_SUFFIX = {
  mastered: 'mastered',
  'in-progress': 'inProgress',
  'needs-review': 'needsReview',
  'not-started': 'notStarted',
};

// Clamp + round a student's percent-of-goal to an integer 0..100.
export function goalPercent(status) {
  const p = Math.round(Number(status?.percent) || 0);
  return Math.max(0, Math.min(100, p));
}

// A student "has a goal" if the rollup says so, or carries goal surahs.
export function hasGoal(status) {
  if (!status) return false;
  if (status.hasGoal) return true;
  if (Array.isArray(status.goalSurahNumbers) && status.goalSurahNumbers.length) return true;
  return Array.isArray(status.surahs) && status.surahs.length > 0;
}

// "Behind plan": the student has a goal but is slipping. The only adherence
// signal the rollup exposes is missed homework, so a student with a goal and at
// least one missed homework is behind; otherwise on-track. No goal → not behind.
export function isBehindPlan(status) {
  if (!hasGoal(status)) return false;
  return (Number(status?.homework?.missed) || 0) > 0;
}

// Plan-state key for the badge / filter: 'noGoal' | 'behindPlan' | 'onTrack'.
export function planState(status) {
  if (!hasGoal(status)) return 'noGoal';
  return isBehindPlan(status) ? 'behindPlan' : 'onTrack';
}

// Does the student have at least one goal surah in the given mastery status?
// An empty/undefined key matches everything (used by the "All statuses" option).
export function hasSurahStatus(status, key) {
  if (!key) return true;
  const surahs = Array.isArray(status?.surahs) ? status.surahs : [];
  return surahs.some((s) => s && s.status === key);
}

// Average % of goal across a group's student rows. Only students who actually
// have a goal count toward the denominator, so an empty halaqa reads 0% rather
// than dragging the average down with goal-less members.
// Returns { percent, withGoal, total, behind }.
export function groupRollup(rows) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const withGoalRows = list.filter(hasGoal);
  const sum = withGoalRows.reduce((acc, r) => acc + goalPercent(r), 0);
  const percent = withGoalRows.length ? Math.round(sum / withGoalRows.length) : 0;
  return {
    percent,
    withGoal: withGoalRows.length,
    total: list.length,
    behind: list.some(isBehindPlan),
  };
}

// Plan state for a whole group (from a groupRollup result): no students with a
// goal → 'noGoal'; any behind → 'behindPlan'; else 'onTrack'.
export function groupPlanState(rollup) {
  if (!rollup || !rollup.withGoal) return 'noGoal';
  return rollup.behind ? 'behindPlan' : 'onTrack';
}
